import React, { ReactElement } from "react";

import { Portal } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Modal, { standardWidth } from "@src/sharedComponents/Modal/Modal";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./SimpleModalWithTextInput.module.css";

interface Form {
  TextInput: string;
}

interface Props {
  title: string;
  text?: string;
  confirmText: string;
  label?: string;
  placeholder?: string;
  defaultTextInputValue?: string;
  closeModal: () => void;
  onSave: (str: string) => void;
  isDisabled?: boolean;
  hideTextInput?: boolean;
  className?: string;
  isOpen?: boolean;
  modalStyle?: React.CSSProperties;
}

const SimpleModalWithTextInput: React.FC<Props> = ({
  title,
  text,
  confirmText,
  label = "",
  placeholder = "",
  defaultTextInputValue = "",
  closeModal,
  onSave,
  isDisabled,
  hideTextInput = false,
  className,
  isOpen = true,
  modalStyle = {},
}: Props): ReactElement => {
  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      TextInput: defaultTextInputValue,
    },
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const save = async () => {
    if (!isValid) {
      return;
    }

    const values = getValues();

    onSave(values.TextInput);
  };

  return (
    <Portal>
      <Modal
        className={toClassName(styles.modal, className)}
        closeModal={closeModal}
        showCloseButton={true}
        isOpen={isOpen}
        modalStyle={{
          ...standardWidth,
          ...modalStyle,
        }}
      >
        <Text className={styles.title} variant="title2">
          {title}
        </Text>

        {text && (
          <Text color="textSubtle" variant="body">
            {text}
          </Text>
        )}

        <div className={styles.formContainer}>
          <FormProvider {...methods}>
            {hideTextInput === false && (
              <div
                style={{
                  position: "relative",
                  zIndex: 1600,
                  pointerEvents: "auto",
                }}
              >
                <FormTextField
                  name="TextInput"
                  label={label}
                  placeholder={placeholder}
                  rules={{
                    validate: {
                      hasMinLength: (input) =>
                        input.length < 3
                          ? "Must be at least 3 characters"
                          : undefined,
                    },
                  }}
                />
              </div>
            )}
          </FormProvider>
        </div>

        <div className={styles.footer}>
          <Button
            text="Cancel"
            onClick={closeModal}
            variant="outlined"
            buttonType="secondary"
            className={styles.actionButton}
          />

          <Button
            text={confirmText}
            variant="contained"
            buttonType="primary"
            onClick={save}
            disabled={
              isDisabled || (hideTextInput === false && (!isValid || !isDirty))
            }
            className={styles.actionButton}
          />
        </div>
      </Modal>
    </Portal>
  );
};

export default SimpleModalWithTextInput;
